import { type Event, Property, type User, User as UserClass } from '@kalos/kalos-rpc';
import {
  Badge,
  DataTable,
  LoadingIcon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';

import { useEventBatchGetQuery } from '../../../../hooks/react-query/useEventsQuery';
import { formatDate, formatTime, navigateNewTab } from '../../../../tools/helpers';

interface CustomerJobsAccordionProps {
  customer: User;
  propertyId?: number;
}

export const CustomerJobsAccordion = ({ customer, propertyId }: CustomerJobsAccordionProps) => {
  // Calculate date range - 2 weeks from now
  const { dateRange, dateTarget } = useMemo(() => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 14); // 2 weeks from now
    endDate.setHours(23, 59, 59, 999); // End of day

    return {
      dateRange: ['<=', format(endDate, 'yyyy-MM-dd HH-mm-ss')],
      dateTarget: ['date_started'],
    };
  }, []);

  // Fetch recent jobs for this customer
  const { data: jobsData, isLoading } = useEventBatchGetQuery({
    filters: {
      customer: UserClass.create({ id: customer.id }),
      ...(propertyId && { property: Property.create({ id: propertyId }) }),
      isActive: 1,
      orderBy: 'id',
      orderDir: 'DESC',
      pageNumber: 0,
      withoutLimit: false,
      dateRange,
      dateTarget,
    },
    enabled: true,
  });

  const handleRowClick = useCallback((event: Event) => {
    navigateNewTab(`/jobs/${event.id}`);
  }, []);

  const tableColumns = useMemo<ColumnDef<Event>[]>(
    () => [
      {
        accessorKey: 'dateStarted',
        header: 'Date / Time',
        meta: {
          className: 'w-24',
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              <div className="text-sm">{formatDate(props.row.original.dateStarted)}</div>
              <div className="text-xs text-gray-600">
                {formatTime(props.row.original.timeStarted)} -{' '}
                {formatTime(props.row.original.timeEnded)}
              </div>
            </div>
          );
        },
      },
      {
        id: 'address',
        header: 'Address',
        meta: {
          className: 'min-w-0 flex-1',
        },
        cell(props) {
          const event = props.row.original;
          return <div className="truncate">{event.property?.address || 'No address'}</div>;
        },
      },
      {
        id: 'status',
        header: 'Status',
        meta: {
          className: 'w-20',
        },
        cell(props) {
          const event = props.row.original;
          return (
            <Badge variant="outline" className="text-xs">
              {event.logJobStatus || 'Unknown'}
            </Badge>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        meta: {
          className: 'w-24',
        },
        cell(props) {
          const event = props.row.original;
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(event);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    View
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View job details</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
    ],
    [handleRowClick],
  );

  const table = useReactTable({
    data: jobsData?.results || [],
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const jobs = jobsData?.results || [];
  const totalCount = jobsData?.totalCount ?? 0;

  return (
    <div className="max-w-full space-y-3">
      <div className="text-sm text-gray-600">
        {isLoading ? 'Loading jobs...' : `${totalCount} job${totalCount === 1 ? '' : 's'} found`}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <LoadingIcon className="size-6" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-4 text-gray-500">
          <p className="text-sm font-medium">No jobs found</p>
          <p className="text-xs">This customer doesn&apos;t have any previous jobs.</p>
        </div>
      ) : (
        <div className="max-w-full overflow-x-auto">
          <div className="min-w-0 [&_table]:w-full [&_table]:table-fixed [&_thead]:static [&_thead]:shadow-none">
            <DataTable table={table} onRowClick={handleRowClick} />
          </div>
        </div>
      )}
    </div>
  );
};
